import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, Typography } from "@mui/material";
import { useContext, useState } from "react";
import useColors from "../../../api/hooks/useColors";
import useMSSP from "../../../api/hooks/useMSSP";
import CustomToolTip from "../../../components/CustomTooltip";
import TextWithExternalLink from "../../../components/ExternalLink";
import LoadingIcon from "../../../components/LoadingIcon";
import StyledDataGrid from "../../../components/StyledDataGrid";
import { EnvFeatureFlagContext } from "../../../contexts/EnvFeaturesContext";
import SvgToIcon from "../../../icons/SvgToIcon";
import { VulnerabilityTypeChip } from "../../../utils/chipsData";
import { daysPastSince } from "../../../utils/timestamp";
import ChipColor from "../../remediation/plan/components/ChipColor";
import { icons } from "../../stack/environment/icons/iconExport";
import IntegrationImage from "../../stack/integrations/components/IntegrationImage";
import { IntegrationsMapping } from "../../stack/integrations/IntegrationsMapping";
import AffectedAssetsCell from "./components/AffectedAssetsCell";
import { TargetTypesCell } from "./components/TargetTypes";
import { getVulnerabilitySource } from "./utils";
import { VulnerabilityExploit } from "./VulnerabilitiesView";

export const mapBreezeScoreToSeverity = (score) => {
  if (score <= 2.5) return "LOW";
  if (score <= 5) return "MEDIUM";
  if (score <= 7.5) return "HIGH";
  return "CRITICAL";
};
const VulnerabilitiesTable = ({
  dataIsLoading,
  vulnerabilities,
  assets = [],
  controls = [],
  gridRef,
  onRowClick,
  topExposureDays,
  filterColumns = [],
  pageSizeOptionsOverride = [10, 20, 50],
  boxSXProps = {},
  vulnerabilitiesAreFetching,
}) => {
  const colors = useColors();
  const [sortModel, setSortModel] = useState([
    {
      field: "breeze_score",
      sort: "desc",
    },
    {
      field: "severity",
      sort: "desc",
    },
  ]);

  const { hasMultipleSelectedTenants } = useMSSP();
  const { isActiveRds } = useContext(EnvFeatureFlagContext);

  const getAssetByID = (assetId) => {
    const asset = assets?.find((asset) => asset?.asset_id === assetId);
    return asset;
  };

  const getToolsOfControls = (controlIDs) => {
    if (!Array.isArray(controlIDs) || controlIDs.length === 0) return [];
    const includedControls = controls.filter((control) =>
      controlIDs.includes(control.control_id)
    );
    const tools = includedControls.map((control) => control.tool);
    return [...new Set(tools)];
  };

  const tableColumns = [
    {
      field: "vulnerability_id",
      headerName: "ID",
      minWidth: 170,
      align: "left",
      headerAlign: "left",
      disableReorder: true,
      renderHeader: () => (
        <Typography sx={{ marginLeft: "8px" }} variant="subtitle2">
          ID
        </Typography>
      ),
      renderCell: (params) => {
        const cisaName = params?.row?.cisa_name;
        return (
          <Box
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: "inherit",
              marginLeft: "8px",
            }}
          >
            <Typography variant="caption">
              {params?.value}
              {cisaName ? ` - ${cisaName}` : ""}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "breeze_score",
      minWidth: 160,
      align: "left",
      headerAlign: "left",
      resizable: true,
      disableReorder: true,
      headerName: "Breeze Score",
      renderHeader: () => (
        <Box display={"flex"} gap={1} alignItems={"center"}>
          <Typography variant="subtitle2">Breeze Score</Typography>
          <CustomToolTip
            title={
              <Typography variant="caption">
                Calculates the priority and severity of vulnerabilities,
                factoring in the ease of remediation, existence of compensating
                controls, and the risk from multiple sources.
              </Typography>
            }
          >
            <HelpOutlineIcon
              sx={{
                color: colors.text_secondary,
                width: "12px",
                height: "12px",
              }}
            />
          </CustomToolTip>
        </Box>
      ),
      valueFormatter: (params) => {
        return params.value;
      },
      valueGetter: (params) => {
        return params.value?.total?.toFixed(1);
      },
      renderCell: (params) => {
        return (
          <ChipColor
            value={params.value}
            type={"vulnerabilitySeverity"}
            width={56}
            keyMapping={mapBreezeScoreToSeverity}
            useValueAsLabel={true}
          />
        );
      },
    },
    {
      field: "severity",
      headerName: "Severity",
      minWidth: 120,
      align: "left",
      headerAlign: "left",
      resizable: true,
      disableReorder: true,
      sortComparator: (v1, v2) => {
        if (!v1 || !v2) return 0;
        const val1 =
          VulnerabilityTypeChip.vulnerabilitySeverity[v1]?.value ?? 0;
        const val2 =
          VulnerabilityTypeChip.vulnerabilitySeverity[v2]?.value ?? 0;
        return val1 - val2;
      },
      valueFormatter: (params) => {
        return params.value;
      },
      renderCell: (params) => {
        return params.value === "NONE" ? (
          <Box sx={{ paddingLeft: "20px" }}>N/A</Box>
        ) : (
          <Box display="flex" alignItems="center" gap={1.2}>
            <ChipColor
              // TODO - For migration. Overtime, "informational" will not exist in the data.
              value={params.value === "informational" ? "LOW" : params.value}
              type={"vulnerabilitySeverity"}
              width={62}
              style={{ padding: "6px", borderRadius: "8px" }}
            />
            {params?.row?.sources &&
              params?.row?.sources?.length > 0 &&
              params?.row?.sources?.every(
                (source) => IntegrationsMapping[source]
              ) && (
                <>
                  <IntegrationImage
                    name={params?.row?.sources?.[0]}
                    width={"18px"}
                    showToolTip={true}
                    newLogo={true}
                  />
                  {params?.row?.sources?.length > 1 && (
                    <CustomToolTip
                      title={
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {params?.row?.sources?.slice(1)?.map((source) => (
                            <IntegrationImage
                              name={source}
                              width={"18px"}
                              showToolTip={true}
                              newLogo={true}
                            />
                          ))}
                        </Box>
                      }
                    >
                      <Typography variant="body2">
                        +{params?.row?.sources?.slice(1)?.length}
                      </Typography>
                    </CustomToolTip>
                  )}
                </>
              )}
          </Box>
        );
      },
    },
    {
      field: "exprt_rating",
      headerName: "ExPRT Rating",
      renderHeader: () => (
        <Box display={"flex"} gap={1} alignItems={"center"}>
          <Typography variant="subtitle2">ExPRT Rating</Typography>
          <CustomToolTip
            title={
              <Typography variant="caption">
                CrowdStrike CVEs with ExPRT rating Low are excluded.
              </Typography>
            }
          >
            <HelpOutlineIcon
              sx={{
                color: colors.text_secondary,
                width: "12px",
                height: "12px",
              }}
            />
          </CustomToolTip>
        </Box>
      ),
      minWidth: 160,
      align: "left",
      headerAlign: "left",
      resizable: true,
      disableReorder: true,
      sortComparator: (v1, v2) => {
        if (!v1 || !v2) return 0;
        const val1 =
          VulnerabilityTypeChip.vulnerabilitySeverity[v1]?.value ?? 0;
        const val2 =
          VulnerabilityTypeChip.vulnerabilitySeverity[v2]?.value ?? 0;
        return val1 - val2;
      },
      valueFormatter: (params) => {
        return params.value || "-";
      },
      renderCell: (params) => {
        return !params.value ? (
          <Typography variant="caption" color={colors.text_secondary}>
            -
          </Typography>
        ) : (
          <Box display="flex" alignItems="center" gap={1.2}>
            <ChipColor
              // TODO - For migration. Overtime, "informational" will not exist in the data.
              value={params.value === "informational" ? "LOW" : params.value}
              type={"vulnerabilitySeverity"}
              width={62}
              style={{ padding: "6px", borderRadius: "8px" }}
            />
          </Box>
        );
      },
    },
    {
      field: "target_types",
      headerName: "Target",
      minWidth: 95,
      align: "left",
      headerAlign: "left",
      resizable: true,
      disableReorder: true,
      valueGetter: (params) => {
        return params?.value?.sort();
      },
      valueFormatter: (params) => {
        return params?.value?.join(", ");
      },
      sortComparator: (v1, v2) => {
        if (!v1 || !v2) return 0;
        if (v1.length !== v2.length) {
          return v1.length - v2.length;
        }

        return v1.join(",").localeCompare(v2.join(","));
      },
      renderCell: (params) => {
        const targetTypes = params?.value;

        return (
          <>
            {targetTypes && targetTypes.length > 0 ? (
              <TargetTypesCell targetTypes={targetTypes} />
            ) : (
              <CustomToolTip
                title={
                  <Typography variant="caption">
                    No target types available
                  </Typography>
                }
              >
                <Typography variant="caption">N/A</Typography>
              </CustomToolTip>
            )}
          </>
        );
      },
    },
    {
      field: "location_source",
      headerName: "Source",
      minWidth: 100,
      align: "left",
      headerAlign: "left",
      resizable: true,
      disableReorder: true,
      valueFormatter: (params) => {
        if (params.value === undefined) return "N/A";

        return params.value;
      },
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems="bottom" width="100%">
            <Typography
              variant="caption"
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params.value || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "has_fix",
      headerName: "Fix",
      minWidth: 70,
      align: "left",
      headerAlign: "left",
      resizable: true,
      disableReorder: true,
      valueFormatter: (params) => {
        if (params.value === undefined) {
          return "N/A";
        }
        return params.value ? "Yes" : "No";
      },
      valueGetter: (params) => {
        return params?.row?.has_fix;
      },
      renderCell: (params) => {
        let displayValue;
        if (params.value === undefined) {
          displayValue = "N/A";
        } else if (params.value) {
          displayValue = "Available";
        } else {
          displayValue = "N/A";
        }
        return (
          <Box display="flex" alignItems="bottom">
            <Typography variant="caption">{displayValue}</Typography>
          </Box>
        );
      },
    },
    {
      field: "nvd_link",
      headerName: "NVD",
      minWidth: 80,
      align: "left",
      headerAlign: "left",
      resizable: true,
      disableReorder: true,
      valueFormatter: (params) => null,
      renderCell: (params) => {
        if (!params?.row?.vulnerability_id?.startsWith("CVE-"))
          return <Typography variant="caption">-</Typography>;

        const { sourceURL, sourceName } = getVulnerabilitySource(
          params?.row?.vulnerability_id
        );

        return (
          <TextWithExternalLink
            linkText={sourceName}
            href={sourceURL}
            tooltipText={`View in ${sourceName}`}
            linkColor={colors.icon_button_inherit_icon}
          />
        );
      },
    },
    {
      field: "exploit",
      headerName: "Exploit",
      minWidth: 100,
      align: "left",
      headerAlign: "left",
      resizable: true,
      disableReorder: true,
      valueFormatter: (params) => {
        let exploit = params.value;
        return exploit === VulnerabilityExploit.AVAILABLE
          ? "Available"
          : exploit === VulnerabilityExploit.HAS_EXPLOIT_IN_THE_WILD
          ? "Available (CVE found in the wild)"
          : "N/A";
      },
      renderCell: (params) => {
        let exploit = params.value;
        return (
          <>
            <Box display="flex" alignItems={"center"} gap={1}>
              <Typography variant="caption">
                {exploit === VulnerabilityExploit.AVAILABLE
                  ? "Available"
                  : exploit === VulnerabilityExploit.HAS_EXPLOIT_IN_THE_WILD
                  ? "Available"
                  : "N/A"}
              </Typography>
              {exploit === VulnerabilityExploit.HAS_EXPLOIT_IN_THE_WILD && (
                <Box visibility={params?.value ? "visible" : "hidden"}>
                  <CustomToolTip
                    title={
                      <Typography variant="caption">
                        This CVE is found in the wild
                      </Typography>
                    }
                  >
                    <Box>
                      <SvgToIcon
                        IconLogo={icons.exploitInTheWildIcon}
                        width={"16px"}
                      />
                    </Box>
                  </CustomToolTip>
                </Box>
              )}
            </Box>
          </>
        );
      },
    },

    {
      field: "published_date",
      headerName: "Exposure Days",
      minWidth: 150,
      align: "left",
      headerAlign: "left",
      resizable: true,
      disableReorder: true,
      valueFormatter: (params) => {
        return params?.value;
      },
      valueGetter: (params) => {
        const daysPass = daysPastSince(
          params?.value?.timestamp_utc_human_readable
        );
        return Number(daysPass) || 0;
      },

      renderCell: (params) => {
        return (
          <Box display="flex" alignItems={"center"}>
            <Box width="50px">
              <Typography variant="caption">{params?.value}</Typography>
            </Box>
            <Box
              visibility={
                params?.value > topExposureDays ? "visible" : "hidden"
              }
              position="relative"
            >
              <CustomToolTip
                title={
                  <Typography variant="caption">
                    Among the top 20% longest-exposed vulnerabilities
                  </Typography>
                }
              >
                <Box>
                  <SvgToIcon
                    IconLogo={icons.severityIcon}
                    style={{
                      marginTop: "2px",
                      width: "26px",
                    }}
                  />
                </Box>
              </CustomToolTip>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "compensating_control_ids",
      headerName: "Compensating Controls",
      minWidth: 190,
      align: "left",
      headerAlign: "left",
      resizable: true,
      sortable: false,
      disableReorder: true,
      valueFormatter: (params) => {
        const tools = getToolsOfControls(params?.value);
        const maxToolsDisplay = 4;
        if (tools.length === 0) {
          return "N/A";
        }
        return tools.length > maxToolsDisplay
          ? `${tools.slice(0, maxToolsDisplay).join(", ")} +${
              tools.length - maxToolsDisplay
            }`
          : tools.join(", ");
      },
      renderCell: (params) => {
        const tools = getToolsOfControls(params?.value);
        const maxToolsDisplay = 4;
        return (
          <>
            {tools.length > 0 ? (
              <Box display="flex" alignItems="center" gap={1.5}>
                {tools
                  .map((tool) => (
                    <IntegrationImage
                      name={tool}
                      width={"24px"}
                      showToolTip={true}
                      newLogo={true}
                    />
                  ))
                  .slice(0, maxToolsDisplay)}
                <CustomToolTip
                  title={
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      {tools.slice(maxToolsDisplay).map((tool) => (
                        <IntegrationImage
                          name={tool}
                          width={"18px"}
                          showToolTip={true}
                          newLogo={true}
                        />
                      ))}
                    </Box>
                  }
                >
                  <Typography variant="body2">
                    {tools.length > maxToolsDisplay
                      ? `+${tools.length - maxToolsDisplay}`
                      : null}
                  </Typography>
                </CustomToolTip>
              </Box>
            ) : (
              <CustomToolTip
                title={
                  <Typography variant="caption">
                    No Compensating Controls available
                  </Typography>
                }
              >
                <Typography variant="caption">N/A</Typography>
              </CustomToolTip>
            )}
          </>
        );
      },
    },
    {
      field: "entity_count",
      headerName: "Affected Entities",
      minWidth: 160,
      align: "left",
      headerAlign: "left",
      resizable: true,
      disableReorder: true,
      valueFormatter: (params) => {
        return params?.value || 0;
      },
      valueGetter: (params) => {
        return params?.value || 0;
      },
      renderCell: (params) => {
        return (
          <Box display="flex" alignItems={"center"}>
            <Typography variant="caption">{params?.value || 0}</Typography>
          </Box>
        );
      },
    },
    {
      field: "related_asset_ids",
      headerName: "Affected Assets",
      minWidth: 160,
      align: "left",
      headerAlign: "left",
      resizable: true,
      disableReorder: true,
      valueFormatter: (params) => {
        const assetsIds = params?.value?.filter((assetID) =>
          assets.some((asset) => asset?.asset_id === assetID)
        );
        const assetNames = assetsIds?.map(
          (assetID) => getAssetByID(assetID)?.name
        );
        return assetNames?.length > 0 ? assetNames.join(", ") : "-";
      },
      sortComparator: (v1, v2) => {
        if (!v1 || !v2) return 0;
        if (v1?.length !== v2?.length) {
          return v2?.length - v1?.length;
        }
        const v1Sorted = v1
          ?.map((assetID) => getAssetByID(assetID)?.name)
          ?.sort();
        const v2Sorted = v2
          ?.map((assetID) => getAssetByID(assetID)?.name)
          ?.sort();

        const v1String = v1Sorted?.join(",") ?? "";
        const v2String = v2Sorted?.join(",") ?? "";
        return v1String.localeCompare(v2String);
      },
      renderCell: (params) => {
        const assetsIds = params?.value?.filter((assetID) =>
          assets.some((asset) => asset?.asset_id === assetID)
        );
        return (
          <>
            {assetsIds && assetsIds?.length > 0 ? (
              <Box
                onClick={(event) => {
                  event.stopPropagation();
                }}
                width="100%"
              >
                <AffectedAssetsCell
                  assets={assetsIds.map((assetID) => getAssetByID(assetID))}
                />
              </Box>
            ) : (
              <Typography variant="caption" color={colors.text_secondary}>
                -
              </Typography>
            )}
          </>
        );
      },
    },
  ];

  const filteredTableColumns = tableColumns.filter(
    (column) =>
      !(hasMultipleSelectedTenants && column.field === "related_asset_ids")
  );

  const displayedColumns =
    filterColumns.length > 0
      ? filteredTableColumns.filter((column) =>
          filterColumns.includes(column.field)
        )
      : filteredTableColumns;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "4px",
          height: "16px",
          padding: "0px 8px",
        }}
        visibility={
          vulnerabilitiesAreFetching && hasMultipleSelectedTenants
            ? "visible"
            : "hidden"
        }
      >
        <LoadingIcon type="circular" size="14px" />
        <Typography variant="caption" fontSize="14px" color="text.secondary">
          Refreshing data
        </Typography>
      </Box>

      <StyledDataGrid
        style={{ cursor: "pointer" }}
        columns={displayedColumns}
        rows={vulnerabilities}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        getRowId={(row) => row.vulnerability_id}
        loading={dataIsLoading}
        apiRef={gridRef}
        onRowClick={(row) => onRowClick(row)}
        initialState={{
          columns: {
            // Hide columns that are not relevant to the RDS environment
            columnVisibilityModel: {
              breeze_score: isActiveRds ? false : true,
              related_asset_ids: isActiveRds ? false : true,
            },
          },
        }}
        pageSizeOptionsOverride={pageSizeOptionsOverride}
        sx={{
          "& .MuiDataGrid-columnHeader": {
            userSelect: "none", // Prevent text selection
          },
        }}
        boxSXProps={boxSXProps}
      />
    </Box>
  );
};

export default VulnerabilitiesTable;
